import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useLang, useTheme, formatApiError, api } from "../lib/providers";
import { Receipt, Translate, Sun, Moon } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ logo: "", name: "" });

  useEffect(() => {
    api.get("/public/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate("/dashboard"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex">
      <div className="hidden lg:flex w-1/2 bg-[var(--brand)] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="flex items-center gap-2 relative z-10">
          <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
            <Receipt weight="fill" size={18} color="white" />
          </div>
          <span className="font-heading font-semibold text-lg">FalkeDeck</span>
        </div>

        <div className="relative z-10">
          <div className="label-overline text-white/60">{branding.name || "Falketek"}</div>
          <h1 className="font-heading text-5xl font-semibold leading-tight tracking-tight mt-4">
            Craft Smarter.<br />Bill Faster.
          </h1>
          <p className="mt-6 text-white/75 text-base leading-relaxed max-w-md">
            Angebote, Aufmaße und ZUGFeRD-konforme Rechnungen — alles an einem Ort. Strukturiert, schnell, kollaborativ.
          </p>
        </div>

        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative z-10 text-xs text-white/60 label-overline">v1.1 · ZUGFeRD ready</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="w-7 h-7 bg-[var(--brand)] flex items-center justify-center">
                <Receipt weight="fill" size={14} color="white" />
              </div>
              <span className="font-heading font-semibold">FalkeDeck</span>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <button onClick={toggleTheme} data-testid="login-theme-toggle"
                className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button onClick={toggleLang} data-testid="login-lang-switch"
                className="flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
                <Translate size={14} />
                <span className="label-overline">{lang.toUpperCase()}</span>
              </button>
            </div>
          </div>

          <div className="label-overline mb-2">01 / {t("login")}</div>
          {branding.logo && (
            <div className="mb-6 flex items-center justify-start">
              <img src={branding.logo} alt={branding.name || "Logo"} data-testid="login-branding-logo"
                className="max-h-16 max-w-[180px] object-contain" />
            </div>
          )}
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-[var(--ink)]">{t("loginTitle")}</h2>
          <p className="text-[var(--muted)] mt-2 text-sm">{t("loginSubtitle")}</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" autoComplete="off">
            <div>
              <label className="label-overline block mb-1.5">{t("email")}</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="w-full h-11 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label-overline">{t("password")}</label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password"
                  className="text-xs text-[var(--brand)] hover:underline"
                >
                  {t("forgotPassword")}
                </Link>
              </div>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full h-11 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none" />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">{error}</div>
            )}
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-11 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white font-medium text-sm rounded-sm transition-colors disabled:opacity-60">
              {loading ? "…" : t("signIn")}
            </button>
          </form>

          <div className="mt-8 text-sm text-[var(--muted)]">
            {lang === "de"
              ? "Kein Konto? Bitte wende dich an deinen Administrator."
              : "No account? Please contact your administrator."}
          </div>
        </div>
      </div>
    </div>
  );
}
